import { memo, useCallback, useMemo } from 'react';
import { Pressable } from 'react-native';
import { Box, UnderMaintenance, useHover } from '@bonanza-social/moonlight';
import { GameProps } from './../Game/Game.types';
import {
  useDispatch,
  useSelector,
  selectAuthenticationStatus,
  State,
} from '@bonanza-social/toolkit/state';
import { launchGame, selectGameIsUnderMaintenance } from '../../../state';
import { getLockedLabel } from './Game.utils';
import JackpotLabel from './JackpotLabel/JackpotLabel';
import FavoritesToggle from './FavoritesToggle/FavoritesToggle';
import LockedLabel from './LockedLabel/LockedLabel';
import Thumbnail from './Thumbnail/Thumbnail';
import { selectGameReleaseDate } from '../../../state';
import Info from './Info/Info';
import { openMaintenanceSheet } from '@bonanza-social/navigation';
import { selectIsTestAccount } from '@bonanza-social/account';

function Game({
  id,
  imageType,
  isCoinsGameplay,
  dynamicSize,
  currencyCode,
  onPress,
  highlighted,
  ...rest
}: GameProps) {
  const dispatch = useDispatch();
  const authStatus = useSelector(selectAuthenticationStatus);
  const releaseDate = useSelector((state: State) =>
    selectGameReleaseDate(state, id),
  );
  const isUnderMaintenance = useSelector((state: State) =>
    selectGameIsUnderMaintenance(state, id),
  );
  const { isHovered, ...hoverTriggers } = useHover();
  const lockedLabel = useMemo(() => getLockedLabel(releaseDate), [releaseDate]);
  const isTestAccount = useSelector(selectIsTestAccount);

  const shouldShowMaintenance =
    isUnderMaintenance && !isTestAccount && authStatus === 'authenticated';

  const handlePress = useCallback(() => {
    if (onPress) {
      onPress(id);
    } else {
      dispatch(launchGame({ id, currencyCode }));
    }
  }, [onPress, id, dispatch, currencyCode]);

  return (
    <Box {...rest}>
      <Box
        overflow="hidden"
        borderRadius="radius-400"
        transform={isHovered ? 'scale(1.05)' : 'scale(1)'}
        {...(highlighted && {
          borderWidth: 'border-width-050',
          borderColor: 'borderGoldSolid',
        })}
      >
        <Pressable onPress={handlePress} {...hoverTriggers}>
          <Thumbnail gameId={id} type={imageType} dynamicSize={dynamicSize} />
        </Pressable>
        {shouldShowMaintenance ? (
          <UnderMaintenance
            onPress={() =>
              openMaintenanceSheet('games', 'maintenanceDescription')
            }
          />
        ) : (
          !isCoinsGameplay && (
            <>
              <JackpotLabel gameId={id} imageType={imageType} />
              {authStatus === 'authenticated' ? (
                <FavoritesToggle gameId={id} imageType={imageType} />
              ) : (
                <Info gameId={id} />
              )}
            </>
          )
        )}
      </Box>
      {lockedLabel ? <LockedLabel label={lockedLabel} /> : null}
    </Box>
  );
}

export default memo(Game);
